import { Button, cn, Counter, EmptyState, ErrorState, PageHeader, Select, Skeleton } from '@pm/ui';
import { addDays, format, isSameDay, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarRange, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { TaskCompactRow } from '@/features/tasks/task-bits';
import { useMe } from '@/lib/auth';
import { useContexts, useWeekly } from '@/lib/queries';

/** SPEC §43 / FRONTEND_UX §10 — SEG..SEX com contagem por dia e drill-down. */
export function WeekPage() {
  const me = useMe();
  const [ref, setRef] = useState(() => new Date());
  const [contextId, setContextId] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const { data: contexts = [] } = useContexts({ status: 'ACTIVE' });
  const { data, isLoading, isError, error, refetch } = useWeekly({
    date: ref.toISOString(),
    context_id: contextId || undefined,
    responsible_user_id: onlyMine ? me.user.id : undefined,
  });
  const today = new Date();
  const day =
    data?.days.find((d) => d.date === selected) ??
    data?.days.find((d) => isSameDay(parseISO(`${d.date}T12:00:00`), today)) ??
    data?.days[0];
  const total = data?.days.reduce((n, d) => n + d.count, 0) ?? 0;

  return (
    <div className="flex flex-col gap-gap-lg">
      <PageHeader
        title="Semana"
        subtitle={
          data
            ? `${format(parseISO(data.week_start), "d 'de' MMMM", { locale: ptBR })} a ${format(addDays(parseISO(data.week_start), 4), "d 'de' MMMM", { locale: ptBR })} · ${total} entregas`
            : 'Entregas previstas de segunda a sexta.'
        }
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOnlyMine((v) => !v)}
              className={cn(onlyMine && 'bg-accent-soft text-accent')}
            >
              Só minhas
            </Button>
            <Select
              value={contextId}
              onChange={(e) => setContextId(e.target.value)}
              className="h-control-sm w-[180px] text-sm"
              aria-label="Contexto"
            >
              <option value="">Todos os contextos</option>
              {contexts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            <span className="inline-flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Semana anterior"
                onClick={() => {
                  setRef(addDays(ref, -7));
                  setSelected(null);
                }}
              >
                <ChevronLeft />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRef(new Date());
                  setSelected(null);
                }}
              >
                Hoje
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Próxima semana"
                onClick={() => {
                  setRef(addDays(ref, 7));
                  setSelected(null);
                }}
              >
                <ChevronRight />
              </Button>
            </span>
          </>
        }
      />
      {isError ? <ErrorState message={error.message} onRetry={() => void refetch()} /> : null}
      {isLoading || !data ? (
        <Skeleton className="h-[120px] rounded-md" />
      ) : (
        <>
          <div className="grid grid-cols-5 gap-gap">
            {data.days.map((d) => {
              const date = parseISO(`${d.date}T12:00:00`);
              const active = day?.date === d.date;
              const isToday = isSameDay(date, today);
              return (
                <button
                  key={d.date}
                  type="button"
                  onClick={() => setSelected(d.date)}
                  className={cn(
                    'flex flex-col gap-1 rounded-md border bg-surface-1 p-3 text-left transition-colors',
                    active
                      ? 'border-accent-border bg-accent-soft'
                      : 'border-border hover:border-border-strong',
                  )}
                >
                  <span
                    className={cn(
                      'font-mono text-xs uppercase tracking-[var(--tracking-caps)]',
                      isToday ? 'text-accent' : 'text-text-3',
                    )}
                  >
                    {format(date, 'EEE', { locale: ptBR })}{' '}
                    <span className="text-text-3">{format(date, 'dd/MM')}</span>
                  </span>
                  <span className="font-mono text-2xl leading-none">{d.count}</span>
                  <span className="flex gap-2 font-mono text-[10px] text-text-3">
                    {d.overdue > 0 ? <span className="text-danger">{d.overdue} atras.</span> : null}
                    {d.completed > 0 ? (
                      <span className="text-success">{d.completed} ok</span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
          <section className="rounded-md border border-border bg-surface-1">
            <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
              <CalendarRange className="size-4 text-text-3" />
              <span className="font-medium">
                {day
                  ? format(parseISO(`${day.date}T12:00:00`), "EEEE, d 'de' MMMM", { locale: ptBR })
                  : '—'}
              </span>
              <Counter tone="neutral">{day?.count ?? 0}</Counter>
            </header>
            {day && day.tasks.length > 0 ? (
              day.tasks.map((t) => <TaskCompactRow key={t.id} task={t} />)
            ) : (
              <EmptyState
                icon={CalendarRange}
                title="Nenhuma entrega neste dia"
                description="Nada com prazo para esta data."
                className="py-8"
              />
            )}
          </section>
        </>
      )}
    </div>
  );
}
